import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@/styles/tailwind-utils';
import { BrandLoader } from '@components/ui/BrandLoader';
import { CloseIcon } from '@lib/icons';

import { useTreatments } from '../hooks/useTreatments';
import type { CreateTreatmentRequest, Specialty, Treatment } from '../types';
import { mapTreatmentsError } from '../utils/mapTreatmentsError';

import { AddTreatmentModal } from './AddTreatmentModal';

// ─── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const isArchived = status === 'archived';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        isArchived
          ? 'bg-muted text-muted-foreground'
          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      )}
    >
      {isArchived
        ? t('specialties.treatments.statusArchived')
        : t('specialties.treatments.statusActive')}
    </span>
  );
}

// ─── Treatment row ─────────────────────────────────────────────────────────────

interface TreatmentRowProps {
  treatment: Treatment;
  isPending: boolean;
  onEdit: () => void;
  onArchive: () => void;
  onRestore: () => void;
}

function TreatmentRow({ treatment, isPending, onEdit, onArchive, onRestore }: TreatmentRowProps) {
  const { t } = useTranslation();
  const isArchived = treatment.status === 'archived';

  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-md border border-border bg-surface px-4 py-3 text-sm transition-opacity',
        isPending && 'pointer-events-none opacity-60',
        isArchived && 'opacity-70',
      )}
    >
      {treatment.color && (
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: treatment.color }}
        />
      )}

      <span className="min-w-0 flex-1 truncate font-medium text-foreground">{treatment.name}</span>

      <span className="shrink-0 tabular-nums text-muted-foreground">
        {t('specialties.treatments.duration', { minutes: treatment.durationMinutes })}
      </span>

      <span className="shrink-0 tabular-nums text-muted-foreground">
        {treatment.price.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}{' '}
        {treatment.currency}
      </span>

      <StatusBadge status={treatment.status} />

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          aria-label={t('specialties.treatments.edit')}
          onClick={onEdit}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <svg
            className="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>

        {isArchived ? (
          <button
            type="button"
            aria-label={t('specialties.treatments.restore')}
            onClick={onRestore}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            aria-label={t('specialties.treatments.archive')}
            onClick={onArchive}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="21 8 21 21 3 21 3 8" />
              <rect x="1" y="3" width="22" height="5" />
              <line x1="10" y1="12" x2="14" y2="12" />
            </svg>
          </button>
        )}
      </div>
    </li>
  );
}

// ─── Specialty section ─────────────────────────────────────────────────────────

function SpecialtyTreatmentSection({ specialty }: { specialty: Specialty }) {
  const { t } = useTranslation();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState<Treatment | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    treatments,
    isLoading,
    fetchError,
    create,
    isCreating,
    createError,
    update,
    isUpdating,
    updateError,
    updatingTreatmentId,
  } = useTreatments(specialty.id);

  const isMutating = isCreating || isUpdating;

  function handleCreate(data: CreateTreatmentRequest) {
    setActionError(null);
    create(data, {
      onSuccess: () => setAddModalOpen(false),
      onError: (err) =>
        setActionError(mapTreatmentsError(err, t, 'specialties.treatments.errors.saveFailed')),
    });
  }

  function handleEdit(data: CreateTreatmentRequest) {
    if (!editingTreatment) return;
    setActionError(null);
    update(
      { treatmentId: editingTreatment.id, body: data },
      {
        onSuccess: () => setEditingTreatment(null),
        onError: (err) =>
          setActionError(mapTreatmentsError(err, t, 'specialties.treatments.errors.saveFailed')),
      },
    );
  }

  function handleArchive(treatment: Treatment) {
    setActionError(null);
    update(
      { treatmentId: treatment.id, body: { status: 'archived' } },
      {
        onError: (err) =>
          setActionError(
            mapTreatmentsError(err, t, 'specialties.treatments.errors.archiveFailed'),
          ),
      },
    );
  }

  function handleRestore(treatment: Treatment) {
    setActionError(null);
    update(
      { treatmentId: treatment.id, body: { status: 'active' } },
      {
        onError: (err) =>
          setActionError(
            mapTreatmentsError(err, t, 'specialties.treatments.errors.archiveFailed'),
          ),
      },
    );
  }

  const addModalError =
    addModalOpen && createError
      ? mapTreatmentsError(createError, t, 'specialties.treatments.errors.saveFailed')
      : null;

  const editModalError =
    editingTreatment && updateError
      ? mapTreatmentsError(updateError, t, 'specialties.treatments.errors.saveFailed')
      : null;

  return (
    <div>
      {/* Section header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {specialty.color && (
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ background: specialty.color }}
            />
          )}
          <h3 className="text-sm font-semibold text-foreground">{specialty.name}</h3>
          {!specialty.isActive && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {t('specialties.inactive')}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setAddModalOpen(true)}
          className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
        >
          <svg
            className="h-3 w-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          {t('specialties.treatments.addButton')}
        </button>
      </div>

      {/* Error banner */}
      {actionError && (
        <div
          role="alert"
          className="mb-3 flex items-start justify-between gap-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
        >
          <span>{actionError}</span>
          <button
            type="button"
            aria-label={t('specialties.dismissError')}
            onClick={() => setActionError(null)}
            className="shrink-0 text-destructive/60 hover:text-destructive"
          >
            <CloseIcon />
          </button>
        </div>
      )}

      {/* Fetch error */}
      {fetchError && !isLoading && (
        <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {mapTreatmentsError(fetchError, t, 'specialties.treatments.errors.loadFailed')}
        </p>
      )}

      {/* Content */}
      {isLoading ? (
        <BrandLoader
          className="py-16"
          title={t('specialties.loading.title')}
          subtitle={t('specialties.loading.subtitle')}
        />
      ) : treatments.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          {t('specialties.treatments.noTreatments')}
        </p>
      ) : (
        <ul className={cn('space-y-2', isMutating && 'pointer-events-none')}>
          {treatments.map((treatment) => (
            <TreatmentRow
              key={treatment.id}
              treatment={treatment}
              isPending={updatingTreatmentId === treatment.id}
              onEdit={() => setEditingTreatment(treatment)}
              onArchive={() => handleArchive(treatment)}
              onRestore={() => handleRestore(treatment)}
            />
          ))}
        </ul>
      )}

      {/* Add modal */}
      <AddTreatmentModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSubmit={handleCreate}
        isSubmitting={isCreating}
        errorMessage={addModalError}
      />

      {/* Edit modal */}
      <AddTreatmentModal
        isOpen={!!editingTreatment}
        onClose={() => setEditingTreatment(null)}
        onSubmit={handleEdit}
        isSubmitting={isUpdating}
        errorMessage={editModalError}
        treatment={editingTreatment ?? undefined}
      />
    </div>
  );
}

// ─── Tab ───────────────────────────────────────────────────────────────────────

interface TreatmentsTabProps {
  specialties: Specialty[];
}

export function TreatmentsTab({ specialties }: TreatmentsTabProps) {
  const { t } = useTranslation();
  const activeSpecialties = specialties.filter((s) => s.isActive);

  if (activeSpecialties.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
        {t('specialties.treatments.noSpecialties')}
      </p>
    );
  }

  return (
    <div className="space-y-8">
      {activeSpecialties.map((specialty) => (
        <SpecialtyTreatmentSection key={specialty.id} specialty={specialty} />
      ))}
    </div>
  );
}
